import React, { useState } from 'react';

import { Card, CardContent, CardHeader, Divider, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import { useAlert } from 'react-alert';

import { EditIcon } from '../components/icons/themes/windows10';
import { PHIAllowListEditModal } from './PHIAllowListEditModal';
import useEnvironment from '../hooks/use-environment';
import { updatePhiAllowlist } from '../actions/environments';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles(() => ({
  allowListHeader: {
    backgroundColor: "white",
    color: "#617984",
  },
  headerText: {
    "& span": {
      color: "#617984",
      fontWeight: "bold",
      textTransform: "uppercase",
      fontSize: 12,
    },
  },
  triangle: {
    borderTop: "4px solid #4050B5",
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    width: 0,
    height: 0,
    transition: ".5s all",
  },
  triangleOpen: {
    transform: "rotate(180deg)",
  },
  listItem: {
    borderBottom: "1px solid #eeeeee",
    overflow: "hidden",
    transition: "flex .5s ease-in-out",
  },
  navigation: {
    paddingTop: 24,
  },
  card: {
    width: "50%",
    minWidth: 800,
  },
  content: {
    padding: 24,
  },
}));

type AllowList = string[];

type AllowLists = {
    event: AllowList;
    user: AllowList;
    group: AllowList;
}

const getAllowListsFromEnvironment: (currentEnvironment: any) => AllowLists = (currentEnvironment) => {
  if (!currentEnvironment.loaded) {
    return {event: [], user: [], group: []};
  }

  const eventProps = Object.keys(currentEnvironment.project.eventPropertySensitivityLevels);
  const userProps = Object.keys(currentEnvironment.project.userPropertySensitivityLevels);
  const groupProps = Object.keys(currentEnvironment.project.groupPropertySensitivityLevels);

  return {event: eventProps, user: userProps, group: groupProps};
}

export const PHIAllowListContainer: React.FC = () => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardHeader title="PHI Allowlist" />
      <CardContent>
        <Divider />
        <br />

        <Table>
          <TableBody>
            <PHIEventAllowList allowListType="event" allowListName="Event Properties" />
            <PHIEventAllowList allowListType="user" allowListName="User Properties" />
            <PHIEventAllowList allowListType="group" allowListName="Group Properties" />
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

interface PHIEventAllowListProps {
  allowListType: 'event' | 'user' | 'group';
  allowListName: string;
}

const PHIEventAllowList: React.FC<PHIEventAllowListProps> = ({allowListType, allowListName}) => {
  const classes = useStyles();
  const alert = useAlert();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const currentEnvironment = useEnvironment();
  const dispatch = useDispatch();

  const allowLists: AllowLists = getAllowListsFromEnvironment(currentEnvironment);

  const allowListedProps = allowLists[allowListType];

  const handleSave = (newAllowListedProps: AllowList) => {
    (dispatch(updatePhiAllowlist(allowListType, newAllowListedProps)) as any)
      .then(() => {
        setIsEditModalOpen(false);
      })
      .catch((error: any) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  let propPreview: string;
  if (!currentEnvironment.loaded) {
    propPreview = "Loading..."
  } else if (allowListedProps.length == 0) {
    propPreview = "No allowlisted properties";
  } else if (allowListedProps.length == 1) {
    propPreview = `"${allowListedProps[0]}" allowlisted`;
  } else if (allowListedProps.length == 2) {
    propPreview = `"${allowListedProps[0]}" and "${allowListedProps[1]}" allowlisted`;
  } else {
    propPreview = `"${allowListedProps[0]}", "${allowListedProps[1]}", and ${allowListedProps.length - 2} other properties allowlisted`;
  }

  return (
    <>
      <TableRow key={allowListType} className="group hover:bg-gray-100">
        <TableCell>
          <div className="text-gray-600 font-bold text-sm truncate">
            {allowListName}
          </div>
        </TableCell>
        <TableCell>
          {propPreview}
        </TableCell>
        <TableCell align="center">
          <div className="flex">
            <div
              className="mr-1 bg-white rounded-full hover:bg-purple-400 fill-gray-700 hover:text-white group cursor-pointer flex justify-center items-center w-8 h-8"
              onClick={() => setIsEditModalOpen(true)}
            >
              <EditIcon />
            </div>
          </div>
        </TableCell>
      </TableRow>
      {isEditModalOpen &&
        <PHIAllowListEditModal open={isEditModalOpen} allowListType={allowListName} allowListedProps={allowListedProps} onSave={handleSave} onClose={() => setIsEditModalOpen(false)} />
      }
    </>
  );
}
